/**
 * Settings sub-nav · 4 groups, 16 items total — canonical mirror of
 * design-source/settings-hub.jsx (note: roadmap text said "8 stub
 * pages" but the canonical structure has 14 stubs + 2 live pages).
 *
 * 2 live in Phase 0: profile, organization.
 * 14 stubs with Phase chips.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  Briefcase,
  Clock,
  Database,
  FileText,
  Filter,
  GitBranch,
  Globe,
  Hash,
  Lock,
  Mail,
  Plug,
  Settings as SettingsIcon,
  Sliders,
  Tag,
  User,
  Users,
} from 'lucide-react';

export type SettingsGroup = 'account' | 'business' | 'integrations' | 'catalogs';

export interface SettingsNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;            // absolute path under the app shell
  phase: number;           // 0 = live in Phase 0
  enabled: boolean;        // false → renders stub, but is still navigable
  group: SettingsGroup;
  description?: string;    // shown on the stub page
}

export const SETTINGS_GROUPS: { id: SettingsGroup; title: string }[] = [
  { id: 'account', title: 'חשבון' },
  { id: 'business', title: 'עסק' },
  { id: 'integrations', title: 'אינטגרציות' },
  { id: 'catalogs', title: 'קטלוגים' },
];

export const SETTINGS_NAV: SettingsNavItem[] = [
  // ---- Account
  { id: 'profile', label: 'פרופיל', icon: User, path: '/settings/profile', phase: 0, enabled: true, group: 'account' },
  { id: 'security', label: 'אבטחה', icon: Lock, path: '/settings/security', phase: 1, enabled: false, group: 'account',
    description: 'ניהול 2FA, sessions פעילים, API tokens ו-webhooks.' },
  { id: 'notifications', label: 'העדפות התראות', icon: Bell, path: '/settings/notifications', phase: 1, enabled: true, group: 'account',
    description: 'מטריצה של 14 סוגי התראות מול 4 ערוצים (in-app, email, push, WhatsApp), כולל שעות שקט.' },

  // ---- Business
  { id: 'organization', label: 'פרטי עסק', icon: Briefcase, path: '/settings/organization', phase: 0, enabled: true, group: 'business' },
  { id: 'locale', label: 'שפה ומטבע', icon: Globe, path: '/settings/locale', phase: 0, enabled: false, group: 'business',
    description: 'ב-Phase 0 הערכים נעולים. עריכה ב-Phase 1.' },
  { id: 'audit', label: 'Audit Log', icon: Clock, path: '/settings/audit', phase: 0, enabled: true, group: 'business' },
  { id: 'data', label: 'ייצוא ומחיקה', icon: Database, path: '/settings/data', phase: 5, enabled: false, group: 'business',
    description: 'ייצוא לידים, דילים, חברות וחשבוניות ל-CSV/JSON. גיבוי ידני. מחיקת חשבון מאובטחת.' },

  // ---- Integrations Hub
  { id: 'integrations', label: 'Integrations Hub', icon: Plug, path: '/settings/integrations', phase: 1, enabled: true, group: 'integrations',
    description: 'ניהול כל החיבורים החיצוניים: iCount, Twilio, Resend, Anthropic, Meta, Google Ads, LinkedIn, Slack.' },

  // ---- Catalogs
  { id: 'cat-services', label: 'קטלוג שירותים', icon: Tag, path: '/settings/catalogs/services', phase: 1, enabled: false, group: 'catalogs',
    description: 'רשימת השירותים שלך עם מחיר ברירת מחדל, מע"מ ויחידת חיוב.' },
  { id: 'cat-pipeline', label: 'שלבי פייפליין', icon: GitBranch, path: '/settings/catalogs/pipeline', phase: 1, enabled: false, group: 'catalogs',
    description: 'הגדר את שלבי הסטטוס של דילים: Discovery, Proposal, Negotiation, Won, Lost.' },
  { id: 'cat-sources', label: 'מקורות לידים', icon: Filter, path: '/settings/catalogs/sources', phase: 1, enabled: true, group: 'catalogs',
    description: 'רשימת ערוצים: WhatsApp, אתר, הפניה, פגישה, AI גילוי אוטומטי.' },
  { id: 'cat-tags', label: 'תגיות', icon: Hash, path: '/settings/catalogs/tags', phase: 1, enabled: true, group: 'catalogs',
    description: 'תגיות חוצות-מודולים (לידים, דילים, מסמכים) עם צבע ייעודי.' },
  { id: 'cat-fields', label: 'שדות מותאמים', icon: Sliders, path: '/settings/catalogs/custom-fields', phase: 1, enabled: false, group: 'catalogs',
    description: 'הגדר שדות מותאמים ללידים, דילים וחברות (טקסט, מספר, תאריך, רשימה).' },
  { id: 'cat-templates', label: 'תבניות וחתימה', icon: Mail, path: '/settings/catalogs/templates', phase: 2, enabled: false, group: 'catalogs',
    description: 'תבניות אימייל, WhatsApp וחתימת אימייל. תמיכה במשתנים דינמיים.' },
  { id: 'cat-invoice', label: 'הגדרות חשבונית', icon: FileText, path: '/settings/catalogs/invoice-settings', phase: 3, enabled: false, group: 'catalogs',
    description: 'מספור, תבנית, צבעים ורגעי שליחה אוטומטית של חשבוניות מ-iCount.' },
  { id: 'cat-team', label: 'צוות והרשאות', icon: Users, path: '/settings/catalogs/team', phase: 1, enabled: true, group: 'catalogs',
    description: 'הזמנת חברי צוות (יואב + דנה כהן ב-Phase 0), תפקידים, הרשאות מודול.' },
];

/** Map app-shell sidebar pseudo-item "settings" → settings sub-page lookup helpers. */
export function findSettingsItem(pathname: string): SettingsNavItem | undefined {
  return SETTINGS_NAV.find((it) => it.path === pathname);
}

export const SETTINGS_ROOT_LABEL = 'הגדרות';
export const SETTINGS_ROOT_ICON: LucideIcon = SettingsIcon;
